import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import LedgerTable, { computeResult } from "../components/LedgerTable";
import { SectionTable, PurchasesTable } from "./DailyMovement";
import { SalesTable as AdvSalesTable, PurchasesTable as AdvPurchasesTable } from "./AdvancedDailyMovement";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { ArrowRight, Save, Loader2, Lock, Printer } from "lucide-react";
import { toast } from "sonner";
import { useDirty, useDirtyGuard, useUnsavedChangesWarning } from "../context/DirtyContext";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { VoucherSlip } from "./Voucher";

function emptyRow() {
    return {
        id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        karat_type: "",
        weight: "",
        karat: "",
        description: "",
        invoice_number: "",
        manufacturing_fee: "",
        gold_received: "",
        fees: "",
        date: "",
    };
}

function sumFees(rows) {
    return rows.reduce((s, r) => {
        const v = parseFloat(r.fees);
        return isFinite(v) ? s + v : s;
    }, 0);
}
function sumGold(rows) {
    return rows.reduce((s, r) => {
        const v = parseFloat(computeResult(r));
        return isFinite(v) ? s + v : s;
    }, 0);
}
function isRowFilled(r) {
    return [
        r.karat_type,
        r.weight,
        r.karat,
        r.description,
        r.invoice_number,
        r.manufacturing_fee,
        r.gold_received,
        r.fees,
        r.date,
    ].some((v) => (v || "").trim());
}

// Stable string snapshot used to detect "dirty" edits.
function snapshot(clientName, creditRows, debitRows) {
    const norm = (rows) =>
        rows.map((r) => ({
            karat_type: r.karat_type || "",
            weight: r.weight || "",
            karat: r.karat || "",
            description: r.description || "",
            invoice_number: r.invoice_number || "",
            manufacturing_fee: r.manufacturing_fee || "",
            gold_received: r.gold_received || "",
            fees: r.fees || "",
            date: r.date || "",
        }));
    return JSON.stringify({
        clientName: (clientName || "").trim(),
        credit: norm(creditRows),
        debit: norm(debitRows),
    });
}

export default function HistoryDetail() {
    const { id, kind: urlKind, empId } = useParams();
    const navigate = useNavigate();
    const { guardedRun } = useDirty();
    const { t } = useLang();
    const { company } = useAuth();
    const karatLock = company?.karat_lock || null;

    // When viewed via `/employees/:empId/daily/:id`, the page is a manager
    // looking at an employee's ledger — strictly read-only, back goes to the
    // employee's list.
    const isManagerView = !!empId;
    const backPath = isManagerView ? `/employees/${empId}` : null;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [createdAt, setCreatedAt] = useState(null);
    const [updatedAt, setUpdatedAt] = useState(null);
    const [kind, setKind] = useState(
        isManagerView
            ? "daily"
            : urlKind === "client" ? "client"
            : urlKind === "daily" ? "daily"
            : urlKind === "voucher_shop" ? "voucher_shop"
            : urlKind === "voucher_customer" ? "voucher_customer"
            : "factory"
    );
    const [dailyData, setDailyData] = useState(null);
    const [dailyDate, setDailyDate] = useState("");
    const [voucherData, setVoucherData] = useState(null);
    const [voucherNumber, setVoucherNumber] = useState(null);

    const [clientName, setClientName] = useState("");
    const [creditRows, setCreditRows] = useState([]);
    const [debitRows, setDebitRows] = useState([]);
    const [baseline, setBaseline] = useState(null);
    const [saveStatus, setSaveStatus] = useState("final");

    useEffect(() => {
        let cancel = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/ledgers/${id}`);
                if (cancel) return;
                const cn = data.client_name || "";
                // Mark all loaded rows as locked — saved data cannot be edited.
                // User can still append new (unlocked) rows below (#row-lock spec).
                const lockRow = (r) => ({ ...r, locked: true });
                const cr = (data.credit_rows || []).length
                    ? data.credit_rows.map(lockRow)
                    : [emptyRow()];
                const dr = (data.debit_rows || []).length
                    ? data.debit_rows.map(lockRow)
                    : [emptyRow()];
                setClientName(cn);
                setCreditRows(cr);
                setDebitRows(dr);
                setBaseline(snapshot(cn, cr, dr));
                setCreatedAt(data.created_at || null);
                setUpdatedAt(data.updated_at || null);
                if (data.kind === "client" || data.kind === "factory" || data.kind === "daily"
                    || data.kind === "voucher_shop" || data.kind === "voucher_customer") setKind(data.kind);
                if (data.kind === "daily") {
                    setDailyData(data.daily_data || null);
                    setDailyDate(data.daily_date || "");
                }
                if (data.kind === "voucher_shop" || data.kind === "voucher_customer") {
                    setVoucherData(data.voucher_data || null);
                    setVoucherNumber(data.voucher_number || (data.voucher_data || {}).voucher_number || null);
                }
                setSaveStatus(data.save_status || "final");
            } catch {
                if (!cancel) {
                    setClientName("");
                    setCreditRows([]);
                    setDebitRows([]);
                    setBaseline(snapshot("", [], []));
                }
            } finally {
                if (!cancel) setLoading(false);
            }
        })();
        return () => {
            cancel = true;
        };
    }, [id]);

    const isClient = kind === "client";

    const totalCredit = useMemo(() => sumFees(creditRows), [creditRows]);
    const totalDebit = useMemo(() => sumFees(debitRows), [debitRows]);
    const netFees = isClient ? 0 : totalCredit - totalDebit;
    const positiveCash = netFees > 0;
    const negativeCash = netFees < 0;

    const goldCredit = useMemo(() => sumGold(creditRows), [creditRows]);
    const goldDebit = useMemo(() => sumGold(debitRows), [debitRows]);
    const netGold = goldCredit - goldDebit;
    const positiveGold = netGold > 0;
    const negativeGold = netGold < 0;

    const currentSnap = useMemo(
        () => snapshot(clientName, creditRows, debitRows),
        [clientName, creditRows, debitRows]
    );
    const isDirty = baseline !== null && currentSnap !== baseline;

    const saveChanges = async () => {
        if (!clientName.trim()) {
            toast.error(t.toastNeedClientEdit);
            return false;
        }
        const stripLock = (r) => {
            const { locked: _l, ...rest } = r;
            return rest;
        };
        const filteredCredit = creditRows.filter(isRowFilled).map(stripLock);
        const filteredDebit = debitRows.filter(isRowFilled).map(stripLock);
        if (filteredCredit.length === 0 && filteredDebit.length === 0) {
            toast.error(t.toastEmptyEdit);
            return false;
        }

        setSaving(true);
        try {
            const payload = {
                kind,
                client_name: clientName.trim(),
                credit_rows: filteredCredit,
                debit_rows: filteredDebit,
                total_credit_fees: totalCredit,
                total_debit_fees: totalDebit,
                net_fees: netFees,
                total_credit_gold: goldCredit,
                total_debit_gold: goldDebit,
                net_gold: netGold,
            };
            const { data } = await api.put(`/ledgers/${id}`, payload);
            setUpdatedAt(data.updated_at || new Date().toISOString());
            // Re-lock all rows after save — user can still add new (unlocked) rows.
            const lockRow = (r) => ({ ...r, locked: true });
            const cr = data.credit_rows && data.credit_rows.length
                ? data.credit_rows.map(lockRow)
                : [emptyRow()];
            const dr = data.debit_rows && data.debit_rows.length
                ? data.debit_rows.map(lockRow)
                : [emptyRow()];
            setCreditRows(cr);
            setDebitRows(dr);
            // Reset baseline so the page is no longer "dirty".
            setBaseline(snapshot(data.client_name || clientName.trim(), cr, dr));
            toast.success(t.toastEditSaved);
            return true;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message || t.toastEditFail);
            return false;
        } finally {
            setSaving(false);
        }
    };

    useDirtyGuard({ isDirty, save: saveChanges, title: t.saveEditsTitle });
    useUnsavedChangesWarning(isDirty);

    const onBackClick = () => guardedRun(() => navigate(backPath || `/history/${kind}`));

    if (loading) {
        return (
            <AppShell>
                <div className="h-64 animate-pulse rounded-2xl bg-neutral-200/60" />
            </AppShell>
        );
    }

    // ── Payment-voucher read-only view ──
    if (kind === "voucher_shop" || kind === "voucher_customer") {
        const vd = voucherData || {};
        const isCustomer = kind === "voucher_customer";
        const onPrint = () => {
            document.body.classList.add("printing-voucher");
            window.print();
            setTimeout(() => document.body.classList.remove("printing-voucher"), 300);
        };
        return (
            <AppShell>
                <div className="mx-auto max-w-3xl space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3 no-print">
                        <div>
                            <button
                                type="button"
                                onClick={() => navigate(`/history/${kind}`)}
                                className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900"
                                data-testid="back-to-history"
                            >
                                <ArrowRight className="h-4 w-4" />
                                {t.backToHistory}
                            </button>
                            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">
                                {isCustomer ? t.voucherCustomerKicker : t.voucherShopKicker}
                            </p>
                            <h1 className="font-heading text-xl font-bold sm:text-2xl">
                                {(isCustomer ? t.voucherCustomerLabel : t.voucherShopLabel)} #{String(voucherNumber || vd.voucher_number || "----").padStart(4, "0")}
                            </h1>
                            <p className="mt-1 text-xs text-neutral-500">
                                {createdAt ? `${t.createdLabel || ""} ${new Date(createdAt).toLocaleString("en-GB")}` : null}
                            </p>
                        </div>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onPrint}
                            data-testid="voucher-print-btn"
                            className="h-9 gap-2"
                        >
                            <Printer className="h-4 w-4" />
                            {t.print || "طباعة"}
                        </Button>
                    </div>

                    <VoucherSlip
                        isRTL={true}
                        data={{
                            ...vd,
                            voucher_number: voucherNumber || vd.voucher_number || "",
                        }}
                        update={null}
                        previewNo={null}
                        companyName={vd.company_name || ""}
                        voucherTypeLabel={isCustomer ? t.voucherCustomerLabel : t.voucherShopLabel}
                        readOnly={true}
                    />
                </div>
            </AppShell>
        );
    }

    // ── Daily-movement read-only view ──
    if (kind === "daily") {
        const dd = dailyData || {};
        const isAdvanced = dd.variant === "advanced";

        if (isAdvanced) {
            const salesRows = dd.sales?.rows || [];
            const purchasesRows = dd.purchases?.rows || [];
            return (
                <AppShell>
                    <div className="mb-4 flex flex-col items-stretch justify-between gap-3 sm:mb-6 sm:flex-row sm:items-end">
                        <div>
                            <button
                                type="button"
                                onClick={() => navigate(backPath || "/history/daily")}
                                className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900"
                                data-testid="back-to-history"
                            >
                                <ArrowRight className="h-4 w-4" />
                                {isManagerView ? t.backToEmployees : t.backToHistory}
                            </button>
                            <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-indigo-700 sm:text-sm">
                                {t.advKicker}
                            </p>
                            <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">
                                {t.advTitle} {dailyDate ? `— ${dailyDate}` : ""}
                            </h1>
                            <p className="mt-1 text-xs text-neutral-500 sm:text-sm">
                                {createdAt ? `${t.createdLabel} ${new Date(createdAt).toLocaleString("en-GB")}` : null}
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                        <span
                            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ring-1 ${
                                saveStatus === "editable"
                                    ? "bg-amber-50 text-amber-800 ring-amber-200"
                                    : "bg-indigo-50 text-indigo-800 ring-indigo-200"
                            }`}
                            data-testid="daily-readonly-badge"
                        >
                            <Lock className="h-3.5 w-3.5" />
                            {saveStatus === "editable" ? t.statusEditableChip : t.dailyReadOnlyBadge}
                        </span>
                        {saveStatus === "editable" && !isManagerView ? (
                            <Button
                                type="button"
                                onClick={() => guardedRun(() => navigate(`/daily?id=${id}`))}
                                className="h-9 gap-2 bg-amber-700 text-white hover:bg-amber-800"
                                data-testid="daily-edit-btn"
                            >
                                <Save className="h-4 w-4" />
                                {t.editRecordBtn}
                            </Button>
                        ) : null}
                        </div>
                    </div>

                    <div
                        className="mb-4 flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50/70 px-3 py-2 text-sm text-indigo-800"
                        data-testid="daily-readonly-note"
                    >
                        <Lock className="h-4 w-4" />
                        <span>{t.advReadOnlyNote}</span>
                    </div>

                    <div className="grid grid-cols-1 gap-5">
                        <AdvSalesTable
                            rows={salesRows}
                            onChange={() => {}}
                            t={t}
                            totals={dd.sales?.total}
                            openings={dd.openings}
                            displayName={dd.section_name}
                            readOnly
                        />
                        <AdvPurchasesTable
                            rows={purchasesRows}
                            onChange={() => {}}
                            t={t}
                            totals={dd.purchases?.total}
                            brokenWeight={String(dd.broken_weight ?? "")}
                            brokenWeightLocked
                            onBrokenWeightChange={() => {}}
                            displayName={dd.section_name}
                            readOnly
                        />
                    </div>
                </AppShell>
            );
        }

        const salesRows = dd.sales?.rows || [];
        const expensesRows = dd.expenses?.rows || [];
        const purchasesRows = dd.purchases?.rows || [];
        return (
            <AppShell>
                <div className="mb-4 flex flex-col items-stretch justify-between gap-3 sm:mb-6 sm:flex-row sm:items-end">
                    <div>
                        <button
                            type="button"
                            onClick={() => navigate(backPath || "/history/daily")}
                            className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900"
                            data-testid="back-to-history"
                        >
                            <ArrowRight className="h-4 w-4" />
                            {isManagerView ? t.backToEmployees : t.backToHistory}
                        </button>
                        <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">
                            {t.dailyKicker}
                        </p>
                        <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">
                            {t.dailyPageTitle} {dailyDate ? `— ${dailyDate}` : ""}
                        </h1>
                        <p className="mt-1 text-xs text-neutral-500 sm:text-sm">
                            {createdAt ? `${t.createdLabel} ${new Date(createdAt).toLocaleString("en-GB")}` : null}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                    <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ring-1 ${
                            saveStatus === "editable"
                                ? "bg-amber-50 text-amber-800 ring-amber-200"
                                : "bg-sky-50 text-sky-800 ring-sky-200"
                        }`}
                        data-testid="daily-readonly-badge"
                    >
                        <Lock className="h-3.5 w-3.5" />
                        {saveStatus === "editable" ? t.statusEditableChip : t.dailyReadOnlyBadge}
                    </span>
                    {saveStatus === "editable" && !isManagerView ? (
                        <Button
                            type="button"
                            onClick={() => guardedRun(() => navigate(`/daily?id=${id}`))}
                            className="h-9 gap-2 bg-amber-700 text-white hover:bg-amber-800"
                            data-testid="daily-edit-btn"
                        >
                            <Save className="h-4 w-4" />
                            {t.editRecordBtn}
                        </Button>
                    ) : null}
                    </div>
                </div>

                <div
                    className="mb-4 flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50/70 px-3 py-2 text-sm text-sky-800"
                    data-testid="daily-readonly-note"
                >
                    <Lock className="h-4 w-4" />
                    <span>{t.dailyReadOnlyNote}</span>
                </div>

                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    <SectionTable
                        title={t.dailySalesTitle}
                        rows={salesRows}
                        onChange={() => {}}
                        columns={["cash", "transfer", "bank"]}
                        t={t}
                        testIdPrefix="ro-sales"
                        totalsRow={dd.sales?.total}
                        spendingRow={dd.sales?.spending}
                        balanceRow={dd.sales?.balance}
                        showSpending
                        readOnly
                    />
                    <SectionTable
                        title={t.dailyExpensesTitle}
                        rows={expensesRows}
                        onChange={() => {}}
                        columns={["cash", "transfer", "bank"]}
                        t={t}
                        testIdPrefix="ro-expenses"
                        totalsRow={dd.expenses?.total}
                        readOnly
                    />
                    <PurchasesTable
                        title={t.dailyPurchasesTitle}
                        rows={purchasesRows}
                        onChange={() => {}}
                        t={t}
                        testIdPrefix="ro-purchases"
                        totalsRow={dd.purchases?.total}
                        spendingRow={dd.purchases?.spending}
                        balanceRow={dd.purchases?.balance}
                        readOnly
                    />
                </div>
            </AppShell>
        );
    }

    return (
        <AppShell>
            <div className="mb-4 flex flex-col items-stretch justify-between gap-3 sm:mb-6 sm:flex-row sm:items-end sm:gap-4">
                <div>
                    <button
                        type="button"
                        onClick={onBackClick}
                        className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900"
                        data-testid="back-to-history"
                    >
                        <ArrowRight className="h-4 w-4" />
                        {t.backToHistory}
                    </button>
                    <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">{t.editKicker}</p>
                    <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">{clientName || t.editFallbackTitle}</h1>
                    <p className="mt-1 text-xs text-neutral-500 sm:text-sm">
                        {createdAt ? `${t.createdLabel} ${new Date(createdAt).toLocaleString("en-GB")}` : null}
                        {updatedAt ? ` • ${t.updatedLabel} ${new Date(updatedAt).toLocaleString("en-GB")}` : null}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        onClick={saveChanges}
                        disabled={saving}
                        className="flex-1 bg-neutral-900 text-white hover:bg-neutral-800 sm:flex-none"
                        data-testid="save-changes-btn"
                    >
                        {saving ? <Loader2 className="ms-1 h-4 w-4 animate-spin" /> : <Save className="ms-1 h-4 w-4" />}
                        {t.saveChanges}
                    </Button>
                </div>
            </div>

            <div className="paper-surface rounded-2xl border border-neutral-200 p-3 shadow-sm sm:rounded-3xl sm:p-5 lg:p-8">
                {/* Client name */}
                <div className="mb-4 flex flex-col gap-3 border-b border-dashed border-neutral-200 pb-4 sm:mb-6 sm:flex-row sm:items-end sm:justify-between sm:pb-6">
                    <div className="flex-1">
                        <label className="mb-1 block text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-500 sm:mb-1.5 sm:text-xs">
                            {t.clientNameLabel}
                        </label>
                        <input
                            value={clientName}
                            onChange={(e) => setClientName(e.target.value)}
                            placeholder={t.clientNamePlaceholder}
                            className="w-full border-0 border-b-2 border-neutral-300 bg-transparent px-0 pb-2 font-heading text-2xl font-bold text-neutral-900 outline-none transition focus:border-amber-600 sm:text-3xl lg:text-4xl"
                            data-testid="edit-client-name-input"
                        />
                    </div>
                </div>

                <div className="space-y-6 sm:space-y-8">
                    <LedgerTable
                        title={t.creditTitle}
                        rows={creditRows}
                        onChange={setCreditRows}
                        variant="credit"
                        kind={kind}
                        testIdPrefix="edit-credit"
                        karatLock={karatLock}
                    />
                    <LedgerTable
                        title={t.debitTitle}
                        rows={debitRows}
                        onChange={setDebitRows}
                        variant="debit"
                        kind={kind}
                        testIdPrefix="edit-debit"
                        karatLock={karatLock}
                    />
                </div>

                {/* Net totals */}
                <div className={`mt-6 grid grid-cols-1 gap-3 sm:mt-8 sm:gap-4 ${isClient ? "" : "lg:grid-cols-2"}`}>
                    <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="inline-block rounded-full bg-amber-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-800 ring-1 ring-amber-200 sm:px-3 sm:text-xs">
                                    {t.goldChip}
                                </div>
                                <div className="mt-1.5 text-xs text-neutral-500 sm:mt-2">{t.netGoldShort}</div>
                            </div>
                            <div
                                className={`rounded-xl px-4 py-2 font-numbers text-2xl font-bold sm:px-5 sm:py-3 sm:text-3xl ${
                                    positiveGold
                                        ? "bg-red-50 text-red-700 ring-1 ring-red-200"
                                        : negativeGold
                                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                        : "bg-neutral-50 text-neutral-500 ring-1 ring-neutral-200"
                                }`}
                                data-testid="edit-net-gold"
                            >
                                {netGold === 0
                                    ? "0"
                                    : (positiveGold ? "+" : "") +
                                      netGold.toLocaleString("en-US", { maximumFractionDigits: 3 })}
                            </div>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-5" hidden={isClient}>
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <div className="inline-block rounded-full bg-neutral-900 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-50 sm:px-3 sm:text-xs">
                                    {t.cashChip}
                                </div>
                                <div className="mt-1.5 text-xs text-neutral-500 sm:mt-2">{t.netFeesShort}</div>
                            </div>
                            <div
                                className={`rounded-xl px-4 py-2 font-numbers text-2xl font-bold sm:px-5 sm:py-3 sm:text-3xl ${
                                    positiveCash
                                        ? "bg-red-50 text-red-700 ring-1 ring-red-200"
                                        : negativeCash
                                        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                                        : "bg-neutral-50 text-neutral-500 ring-1 ring-neutral-200"
                                }`}
                                data-testid="edit-net-fees"
                            >
                                {netFees === 0
                                    ? "0"
                                    : (positiveCash ? "+" : "") +
                                      netFees.toLocaleString("en-US", { maximumFractionDigits: 3 })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
